import { Fragment, memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { NakedUl, Text, TextLink } from '@frontend/design-system';
import { useChartContext } from '../chart.provider';
import { Card } from './card';
import { ColumnHeaders } from './column-headers';
import { NameHeader } from './name-header';
import { ScrollButtons } from './scroll-buttons';
import { StickyColumn } from './sticky-column';
import { styles } from './styles';
import { LeaderboardDatum, LeaderboardProps, SortType, Sorted } from './types';

// TODO :: calculate it based on the content in one of the next PR
const MAX_HEIGHT = 257 + 42; // 257 is the height of 3 rows and 42 is the height of column headers

const sortByKey = ({ data, key, sortType }: { data: LeaderboardDatum[]; key: string; sortType: SortType }) => {
  return [...data].sort((a, b) => {
    const aValue = key === 'name' ? a.name : a.values[key]?.primaryValue ?? 0;
    const bValue = key === 'name' ? b.name : b.values[key]?.primaryValue ?? 0;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortType === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortType === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });
};

export const Leaderboard = memo(
  ({
    appearance = {},
    columnHeaderLabels,
    data = [],
    defaultSort,
    isPivotMode = false,
    onClick,
    pivotMetricKeys = [],
    trackingIdBase,
    renderDataCard,
  }: LeaderboardProps) => {
    const {
      comparisonType = 'rows',
      highestMetric,
      lowestMetric,
      minCardWidth = 180,
      stripeOddRows = false,
      isBorderless = false,
    } = appearance;
    const { t } = useTranslation('analytics');
    const { isSmallMode } = useIsSmallMode();
    const { isLoading } = useChartContext();
    const ulRef = useRef<HTMLUListElement>(null);
    const [showMore, setShowMore] = useState<boolean | null>(null);
    const [isShowingAllRows, setIsShowingAllRows] = useState<boolean>(isSmallMode && !isPivotMode);
    const [columnsOrder, setColumnsOrder] = useState<string[]>([]);
    const [sorted, setSorted] = useState<Sorted | null>(defaultSort ?? null);
    const [collapsedHeight, setCollapsedHeight] = useState(0);

    const isRowComparison = comparisonType === 'rows';

    /**
     * Pivot Mode transforms the data structure.
     * Default: Each row is a location (e.g., "Beverly Hills"), and columns are metrics (e.g., "Scheduled Rate").
     * Pivot: Each row becomes a metric (e.g., "Scheduled Rate"), and columns become locations (e.g., "Beverly Hills").
     * This is useful for mobile views to compare all locations for a single metric.
     */
    const pivotData = useMemo(() => {
      if (!isPivotMode || !pivotMetricKeys.length) return data;

      const metricsMap: Record<string, LeaderboardDatum> = {};
      pivotMetricKeys.forEach((metricKey) => {
        metricsMap[metricKey] = { id: metricKey, name: metricKey, values: {} };
      });

      data.forEach((locationRow) => {
        const locationId = locationRow.id ?? locationRow.name;
        pivotMetricKeys.forEach((metricKey) => {
          const cell = locationRow.values[metricKey] ?? { primaryValue: 0 };
          metricsMap[metricKey].values[locationId] = cell;
        });
      });

      return Object.values(metricsMap);
    }, [isPivotMode, pivotMetricKeys, data]);

    // `renderData` is the data structure that will be used for rendering, either original or pivoted.
    const renderData = isPivotMode ? pivotData : data;

    const sortedData = useMemo(() => {
      if (!sorted || isPivotMode) return renderData;
      return sortByKey({ data: renderData, key: sorted.key, sortType: sorted.type });
    }, [renderData, sorted, isPivotMode]);

    const baseColumns = useMemo(() => {
      if (!renderData.length) return [];
      return Object.keys(renderData[0].values ?? {});
    }, [renderData]);

    // In pivot mode, we derive header labels from the original data's location names.
    const derivedHeaderLabels = useMemo(() => {
      if (!isPivotMode) return columnHeaderLabels;
      const locationMap = Object.fromEntries(data.map((row) => [row.id ?? row.name, row.name]));
      return { ...locationMap, ...(columnHeaderLabels ?? {}) };
    }, [isPivotMode, data, columnHeaderLabels]);

    /**
     * - In pivot we compute across columns for a single metric row.
     * - In non-pivot we compute across rows for a single metric column.
     */
    const minAndMax = useMemo(() => {
      if (!highestMetric && !lowestMetric) return null;

      if (isPivotMode) {
        const result = {
          highestValue: Number.NEGATIVE_INFINITY,
          highestValueNames: [] as string[],
          lowestValue: Number.POSITIVE_INFINITY,
          lowestValueNames: [] as string[],
        };

        const highestMetricRow = renderData.find((row) => row.name === highestMetric);

        if (highestMetricRow) {
          const highestColumns = Object.entries(highestMetricRow.values).reduce(
            (acc, [locationId, cell]) => {
              const value = cell?.primaryValue ?? Number.NEGATIVE_INFINITY;
              if (value > acc.value) {
                return { value, ids: [locationId] };
              }
              if (value === acc.value) {
                acc.ids.push(locationId);
              }
              return acc;
            },
            { value: Number.NEGATIVE_INFINITY, ids: [] as string[] }
          );

          result.highestValue = highestColumns.value;
          result.highestValueNames = highestColumns.ids.map((id) => derivedHeaderLabels?.[id] || id);
        }

        const lowestMetricRow = renderData.find((row) => row.id === lowestMetric);

        if (lowestMetricRow) {
          const lowestColumns = Object.entries(lowestMetricRow.values).reduce(
            (acc, [locationId, cell]) => {
              const value = cell?.primaryValue ?? Number.POSITIVE_INFINITY;
              if (value < acc.value) {
                return { value, ids: [locationId] };
              }
              if (value === acc.value) {
                acc.ids.push(locationId);
              }
              return acc;
            },
            { value: Number.POSITIVE_INFINITY, ids: [] as string[] }
          );

          result.lowestValue = lowestColumns.value;
          result.lowestValueNames = lowestColumns.ids.map((id) => derivedHeaderLabels?.[id] || id);
        }

        return result;
      }

      return renderData.reduce(
        (acc, { name, values }) => {
          const highestValue = highestMetric ? values[highestMetric]?.primaryValue ?? 0 : 0;
          const lowestValue = lowestMetric ? values[lowestMetric]?.primaryValue ?? 0 : 0;

          if (highestValue > acc.highestValue) {
            acc.highestValue = highestValue;
            acc.highestValueNames = [name];
          } else if (highestValue === acc.highestValue) {
            acc.highestValueNames.push(name);
          }

          if (lowestValue < acc.lowestValue) {
            acc.lowestValue = lowestValue;
            acc.lowestValueNames = [name];
          } else if (lowestValue === acc.lowestValue) {
            acc.lowestValueNames.push(name);
          }

          return acc;
        },
        {
          highestValue: 0,
          highestValueNames: [] as string[],
          lowestValue: Infinity,
          lowestValueNames: [] as string[],
        }
      );
    }, [highestMetric, lowestMetric, isPivotMode, renderData, derivedHeaderLabels]);

    const sortColumnsByMetric = useCallback(
      ({ metricKey, sortType, columns }: { metricKey: string; sortType: SortType; columns: string[] }) => {
        const metricRow =
          renderData.find((row) => row.id === metricKey) || renderData.find((row) => row.name === metricKey) || null;

        if (!metricRow) return columns;

        return [...columns].sort((a, b) => {
          const av = metricRow.values[a]?.primaryValue ?? 0;
          const bv = metricRow.values[b]?.primaryValue ?? 0;
          return sortType === 'asc' ? av - bv : bv - av;
        });
      },
      [renderData]
    );

    const handleShowMore = useCallback(() => {
      if (isShowingAllRows) {
        // If already showing all rows, hide as per max height
        const height = isPivotMode && isSmallMode ? collapsedHeight : MAX_HEIGHT;
        ulRef.current?.style.setProperty('max-height', `${height}px`);
      } else {
        // If not showing all rows, show all
        ulRef.current?.style.setProperty('max-height', `${ulRef.current.scrollHeight}px`);
      }

      // Toggle the state
      setIsShowingAllRows(!isShowingAllRows);
    }, [collapsedHeight, isSmallMode, isPivotMode, isShowingAllRows]);

    const handleSortClick = useCallback(
      (metricId: string) => {
        if (isPivotMode) {
          const nextType: SortType = !sorted ? 'desc' : sorted.type === 'desc' ? 'asc' : 'desc';
          const nextCols = sortColumnsByMetric({
            metricKey: metricId,
            sortType: nextType,
            columns: columnsOrder.length ? columnsOrder : baseColumns,
          });
          setColumnsOrder(nextCols);
          setSorted({ key: metricId, type: nextType });
          return;
        }

        // Cancel the sort if the column is sorted in ascending order
        if (sorted?.key === metricId && sorted.type === 'asc') {
          setSorted(null);
          return;
        }

        // If already sorted by the same column, toggle the sort order
        if (sorted?.key === metricId) {
          setSorted({ key: metricId, type: sorted.type === 'asc' ? 'desc' : 'asc' });
          return;
        }

        // Sort by the given column
        setSorted({ key: metricId, type: 'desc' });
      },
      [baseColumns, columnsOrder, isPivotMode, sortColumnsByMetric, sorted]
    );

    // Effect for columnsOrder recompute on sort
    useLayoutEffect(() => {
      let cols = baseColumns;
      if (isPivotMode && sorted?.key) {
        cols = sortColumnsByMetric({ metricKey: sorted.key, sortType: sorted.type ?? 'desc', columns: baseColumns });
      }
      setColumnsOrder(cols);
    }, [baseColumns, isPivotMode, sortColumnsByMetric, sorted?.key, sorted?.type]);

    // Effect for sizing + initial scroll only when data shape changes
    useLayoutEffect(() => {
      const root = ulRef.current;

      if (!root) return;

      if (!(isPivotMode && isSmallMode)) {
        root.scrollTo({ left: 0 });
        setShowMore(!isSmallMode && renderData.length > 3);

        if (isShowingAllRows || isSmallMode) {
          if (isSmallMode) {
            root.style.setProperty('max-height', 'none');
            setIsShowingAllRows(true);
            return;
          }
          root.style.setProperty('max-height', `${root.scrollHeight}px`);
        }
        return;
      }

      const animationFrameId = requestAnimationFrame(() => {
        const rows = root.querySelectorAll('.pivot-row');
        const hasOverflowCols = Object.keys(renderData[0]?.values || {}).length > 3; // show more if there are more than 3 columns in the pivot row
        setShowMore(hasOverflowCols);

        if (rows && rows.length > 0) {
          const heightOfFirstThree = Array.from(rows)
            .slice(0, 3)
            .reduce((acc, row) => acc + (row as HTMLElement).offsetHeight, 0);
          setCollapsedHeight(heightOfFirstThree);
          if (!isShowingAllRows) {
            root.style.setProperty('max-height', `${heightOfFirstThree}px`);
          }
        }
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, [data.length, minCardWidth, isSmallMode, isShowingAllRows, isPivotMode, renderData]);

    return (
      <div css={styles.mainWrapper}>
        <NakedUl
          className={isRowComparison ? 'rows-comparison-ul' : 'columns-comparison-ul'}
          css={styles.ul}
          ref={ulRef}
          style={{ maxHeight: MAX_HEIGHT }}
        >
          {!!data.length && ((isRowComparison && !isSmallMode) || !isRowComparison) && (
            <ColumnHeaders
              columnsOrder={columnsOrder}
              labels={columnHeaderLabels}
              onSortClick={data.length > 1 ? handleSortClick : undefined}
              showNameColumn={!isSmallMode}
              sorted={sorted}
              stripeOddRows={stripeOddRows}
              trackingIdBase={trackingIdBase}
            />
          )}
          {sortedData.map(({ id, name, values, bottomElement }, index) => {
            return (
              <li
                className={
                  isPivotMode ? 'pivot-comparison-li' : isRowComparison ? 'rows-comparison-li' : 'columns-comparison-li'
                }
                css={styles.leaderboardRow({ stripeOddRows })}
                key={name}
              >
                {!isSmallMode && (
                  <StickyColumn
                    highestMetric={highestMetric}
                    lowestMetric={lowestMetric}
                    minAndMax={minAndMax}
                    name={name}
                    serialNo={index + 1}
                    bottomElement={bottomElement}
                  />
                )}
                <div className='data-cards-wrapper' css={styles.dataCardsWrapper({ stripeOddRows })}>
                  {columnsOrder.map((key, index) => {
                    const { onClick: cardClick, ...rest } = values[key] || {};

                    if (isPivotMode) {
                      return (
                        <div key={key} css={styles.pivotLeaderboardRow} className='pivot-row'>
                          {index === 0 && (
                            <ColumnHeaders
                              columnsOrder={[name]}
                              labels={columnHeaderLabels}
                              onSortClick={data.length > 1 ? handleSortClick : undefined}
                              showNameColumn={!isSmallMode}
                              sorted={sorted}
                              stripeOddRows={stripeOddRows}
                              trackingIdBase={trackingIdBase}
                            />
                          )}
                          <div className='metric-card-wrapper'>
                            <NameHeader
                              className='card-space'
                              columnId={id || name}
                              highestMetric={highestMetric}
                              lowestMetric={lowestMetric}
                              minAndMax={minAndMax}
                              name={derivedHeaderLabels?.[key] || key}
                              bottomElement={data?.find((item) => item.id === key)?.bottomElement}
                            />
                            {renderDataCard && renderDataCard({ data, rowId: key, columnId: id || name })}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <Fragment key={key}>
                        {isSmallMode && isRowComparison && index === 0 && (
                          <NameHeader
                            className='card-space'
                            columnId={key}
                            highestMetric={highestMetric}
                            lowestMetric={lowestMetric}
                            minAndMax={minAndMax}
                            name={name}
                            bottomElement={bottomElement}
                          />
                        )}
                        <div className='card-space' key={key} style={{ minWidth: minCardWidth }}>
                          {isSmallMode &&
                            (isRowComparison ? (
                              <Text color='subdued' size='medium'>
                                {columnHeaderLabels?.[key] ?? key}
                              </Text>
                            ) : (
                              <NameHeader
                                columnId={key}
                                highestMetric={highestMetric}
                                lowestMetric={lowestMetric}
                                minAndMax={minAndMax}
                                name={name}
                                style={{ marginBottom: theme.spacing(1) }}
                                bottomElement={bottomElement}
                              />
                            ))}
                          <Card
                            {...rest}
                            columnId={key}
                            noBorder={isBorderless}
                            rowId={id ?? name}
                            onClick={cardClick ?? onClick}
                            trackingIdBase={trackingIdBase}
                          />
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </NakedUl>
        {showMore && (
          <TextLink
            className='toggle-more'
            onClick={handleShowMore}
            trackingId={`${trackingIdBase}-toggle-show-more`}
            weight='bold'
          >
            {isShowingAllRows ? t('Show Less') : t('Show More')}
            <Icon name={isShowingAllRows ? 'caret-up-small' : 'caret-down-small'} />
          </TextLink>
        )}

        {isSmallMode &&
          !isLoading &&
          ((!isRowComparison && columnsOrder.length > 2) || (isRowComparison && data.length > 2)) && (
            <ScrollButtons
              columnsOrder={columnsOrder}
              dataLength={renderData.length}
              isRowComparison={isRowComparison}
              scrollView={ulRef.current}
            />
          )}
      </div>
    );
  }
);

Leaderboard.displayName = 'Leaderboard';
